import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';
import { Coupon, Shop, SubscriberCouponMarketingSend, MessageType } from '@castiron/domain';
import {
  couponRepository,
  marketingSendRepository,
  sendLogRepository,
  shopRepository,
  transactionRepository,
} from '../../domain';
import { handleReducerError } from './errorHandler';

export type AutomationProps = {
  config: {
    enabled: boolean;
    couponId?: string;
    sendToOrdersWithin60Days?: boolean;
  };
  stats: {
    totalSends: number;
    totalRevenue: number;
    lastSent?: number;
  };
  coupon?: Coupon;
};

export interface AutomationsState {
  loading: boolean;
  hasLoaded: boolean;
  automations: {
    thankYou: AutomationProps;
    newsletter: AutomationProps;
    welcomeSeries: AutomationProps;
    abandonedCheckout: AutomationProps;
    abandonedOrderForm: AutomationProps;
  };
}

const initialState: AutomationsState = {
  loading: false,
  hasLoaded: false,
  automations: null,
};

//utils
export const sendFrequencyLabel = frequency =>
  frequency === 'weekly' ? 'weekly' : frequency === 'biweekly' ? 'biweekly' : 'monthly';

const getCoupon = async (config): Promise<Coupon> => {
  if (config?.couponId) {
    const coupon = await Promise.resolve(couponRepository.get(config.couponId));
    return coupon;
  } else return;
};

export const getTotalSends = async (sendType: MessageType, shopId: string) => {
  const sends = await Promise.resolve(sendLogRepository.findSendsByType(shopId, sendType));
  const total = sendType === 'welcome_series' ? _.uniqBy(sends, 'customerId')?.length : sends.length;
  return total;
};

export const getTotalSendsAndLastSent = async (sendType: MessageType, shopId: string) => {
  const sends = await Promise.resolve(sendLogRepository.findSendsByType(shopId, sendType));
  const total = sendType === 'welcome_series' ? _.uniqBy(sends, 'customerId')?.length : sends.length;
  return {
    total,
    lastSent: sends.length > 0 ? sends[0]?.createdAt : null,
  };
};

export const getTotalRevenueBySend = async (utmSource: string, findBy: 'source' | 'campaign', shopId: string) => {
  const transactions = await Promise.resolve(transactionRepository.findTransactionsByUtm(shopId, utmSource, findBy));

  const totals = transactions.map(t => t.totals.totalWithTax);
  const revenue = !_.isEmpty(totals) ? totals.reduce((acc, curr) => acc + curr, 0) : 0;

  return revenue;
};

//individual automations
const getThankYouAutomation = async (shop: Shop): Promise<AutomationProps> => {
  const config = shop?.config?.marketing?.thankYouCoupon;
  const coupon = await Promise.resolve(getCoupon(config));

  const totalSends = await getTotalSends('thank_you_coupon', shop.id);
  const totalRevenue = await getTotalRevenueBySend('thank_you_coupon', 'campaign', shop.id);

  return {
    config,
    coupon,
    stats: {
      totalSends,
      totalRevenue,
    },
  };
};

const getNewsletterAutomation = async (shop: Shop): Promise<AutomationProps> => {
  const config = shop?.config?.marketing?.newsletterSeries;

  const newsletterSeriesTotalP = getTotalSendsAndLastSent('newsletter_series', shop.id);
  const newsletterSeries1TotalRevenueP = getTotalRevenueBySend('newsletter_series_1', 'campaign', shop.id);
  const newsletterSeries2TotalRevenueP = getTotalRevenueBySend('newsletter_series_2', 'campaign', shop.id);
  const originalNewsletterTotalSendsP = getTotalSends('newsletter', shop.id);
  const originalNewsletterTotalRevenueP = getTotalRevenueBySend('shop-newsletter', 'source', shop.id);

  const [
    newsletterSeriesTotal,
    newsletterSeries1TotalRevenue,
    newsletterSeries2TotalRevenue,
    originalNewsletterTotalSends,
    originalNewsletterTotalRevenue,
  ] = await Promise.all([
    newsletterSeriesTotalP,
    newsletterSeries1TotalRevenueP,
    newsletterSeries2TotalRevenueP,
    originalNewsletterTotalSendsP,
    originalNewsletterTotalRevenueP,
  ]);

  const totalSends = newsletterSeriesTotal.total + originalNewsletterTotalSends;
  const totalRevenue = newsletterSeries1TotalRevenue + newsletterSeries2TotalRevenue + originalNewsletterTotalRevenue;

  return {
    config,
    stats: {
      totalSends: totalSends || 0,
      totalRevenue: totalRevenue || 0,
      lastSent: newsletterSeriesTotal.lastSent,
    },
  };
};

const getWelcomeSeriesAutomation = async (shop: Shop): Promise<AutomationProps> => {
  const config = shop?.config?.marketing?.welcomeSeriesCoupon;
  const coupon = await Promise.resolve(getCoupon(config));

  const welcomeSeriesTotalSendsP = getTotalSends('welcome_series', shop.id);
  const welcomeSeriesTotalRevenueSubscribersP = getTotalRevenueBySend(
    'welcome_series_new_subscriber',
    'campaign',
    shop.id,
  );
  const welcomeSeriesTotalRevenueSocialP = getTotalRevenueBySend('welcome_series_social', 'campaign', shop.id);
  const welcomeSeriesTotalRevenueProductsP = getTotalRevenueBySend('welcome_series_products', 'campaign', shop.id);
  const SubscriberTotalSendsP = getTotalSends('subscriber_coupon', shop.id);
  const SubscriberTotalRevenueP = getTotalRevenueBySend('subscriber-coupon', 'source', shop.id);

  const [
    welcomeSeriesTotalSends,
    welocmeSeriesTotalRevenueSubscribers,
    welcomeSeriesTotalRevenueSocial,
    welcomeSeriesTotalRevenueProducts,
    SubscriberTotalSends,
    SubscriberTotalRevenue,
  ] = await Promise.all([
    welcomeSeriesTotalSendsP,
    welcomeSeriesTotalRevenueSubscribersP,
    welcomeSeriesTotalRevenueSocialP,
    welcomeSeriesTotalRevenueProductsP,
    SubscriberTotalSendsP,
    SubscriberTotalRevenueP,
  ]);
  const totalSends = welcomeSeriesTotalSends + SubscriberTotalSends;
  const totalRevenue =
    welocmeSeriesTotalRevenueSubscribers +
    welcomeSeriesTotalRevenueSocial +
    welcomeSeriesTotalRevenueProducts +
    SubscriberTotalRevenue;

  if (!!coupon) {
    return {
      config,
      coupon,
      stats: {
        totalSends: totalSends || 0,
        totalRevenue: totalRevenue || 0,
      },
    };
  } else {
    let marketingSendCoupon;

    const subscriberMarketingSend = await Promise.resolve(
      marketingSendRepository.findActiveOfTypeForShop(shop?.id, 'subscriber_coupon'),
    );
    if (!!subscriberMarketingSend.length) {
      const marketingSend = subscriberMarketingSend[0] as SubscriberCouponMarketingSend;
      marketingSendCoupon = await Promise.resolve(couponRepository.get(marketingSend.couponId));

      return {
        config,
        coupon: marketingSendCoupon,
        stats: {
          totalSends: totalSends || 0,
          totalRevenue: totalRevenue || 0,
        },
      };
    } else {
      return {
        config,
        stats: {
          totalSends: totalSends || 0,
          totalRevenue: totalRevenue || 0,
        },
      };
    }
  }
};

const getAbandonedCheckoutAutomation = async (shop: Shop): Promise<AutomationProps> => {
  const config = shop?.config?.marketing?.abandonedCheckout;
  const coupon = await Promise.resolve(getCoupon(config));

  const abandonedCheckoutTotalP = getTotalSendsAndLastSent('abandoned_checkout', shop.id);
  const abandonedCheckout1TotalRevenueP = getTotalRevenueBySend('abandoned_checkout_1', 'campaign', shop.id);
  const abandonedCheckout2TotalRevenueP = getTotalRevenueBySend('abandoned_checkout_2', 'campaign', shop.id);

  const [abandonedCheckoutTotal, abandonedCheckout1TotalRevenue, abandonedCheckout2TotalRevenue] = await Promise.all([
    abandonedCheckoutTotalP,
    abandonedCheckout1TotalRevenueP,
    abandonedCheckout2TotalRevenueP,
  ]);
  const totalSends = abandonedCheckoutTotal.total;
  const totalRevenue = abandonedCheckout1TotalRevenue + abandonedCheckout2TotalRevenue;

  return {
    config,
    coupon,
    stats: {
      totalSends: totalSends || 0,
      totalRevenue: totalRevenue || 0,
      lastSent: abandonedCheckoutTotal.lastSent,
    },
  };
};

const getAbandonedOrderFormAutomation = async (shop: Shop): Promise<AutomationProps> => {
  const config = shop?.config?.marketing?.abandonedOrderForm;

  const abandonedOrderFormTotalP = getTotalSendsAndLastSent('abandoned_order_form', shop.id);
  const abandonedOrderForm1TotalRevenueP = getTotalRevenueBySend('abandoned_order_form_1', 'campaign', shop.id);
  const abandonedOrderForm2TotalRevenueP = getTotalRevenueBySend('abandoned_order_form_2', 'campaign', shop.id);

  const [
    abandonedOrderFormTotal,
    abandonedOrderForm1TotalRevenue,
    abandonedOrderForm2TotalRevenue,
  ] = await Promise.all([abandonedOrderFormTotalP, abandonedOrderForm1TotalRevenueP, abandonedOrderForm2TotalRevenueP]);
  const totalSends = abandonedOrderFormTotal.total;
  const totalRevenue = abandonedOrderForm1TotalRevenue + abandonedOrderForm2TotalRevenue;

  return {
    config,
    stats: {
      totalSends: totalSends || 0,
      totalRevenue: totalRevenue || 0,
      lastSent: abandonedOrderFormTotal.lastSent,
    },
  };
};

//all automations
const getAutomations = async (id: string) => {
  const shop = await Promise.resolve(shopRepository.get(id));

  const thankYouAutomationP = getThankYouAutomation(shop);
  const newsletterAutomationP = getNewsletterAutomation(shop);
  const welcomeSeriesAutomationP = getWelcomeSeriesAutomation(shop);
  const abandonedCheckoutAutomationP = getAbandonedCheckoutAutomation(shop);
  const abandonedOrderFormAutomationP = getAbandonedOrderFormAutomation(shop);

  const [thankYou, newsletter, welcomeSeries, abandonedCheckout, abandonedOrderForm] = await Promise.all([
    thankYouAutomationP,
    newsletterAutomationP,
    welcomeSeriesAutomationP,
    abandonedCheckoutAutomationP,
    abandonedOrderFormAutomationP,
  ]);

  return {
    thankYou,
    newsletter,
    welcomeSeries,
    abandonedCheckout,
    abandonedOrderForm,
  };
};

export const getAutomationsAction = createAsyncThunk('automations/getAutomations', getAutomations);
export const getThankYouAutomationAction = createAsyncThunk('automations/getThankYouAutomation', getThankYouAutomation);
export const getNewsletterAutomationAction = createAsyncThunk(
  'automations/getNewsletterAutomation',
  getNewsletterAutomation,
);
export const getWelcomeSeriesAutomationAction = createAsyncThunk(
  'automations/getWelcomeSeriesAutomation',
  getWelcomeSeriesAutomation,
);
export const getAbandonedCheckoutAutomationAction = createAsyncThunk(
  'automations/getAbandonedCheckoutAutomation',
  getAbandonedCheckoutAutomation,
);
export const getAbandonedOrderFormAutomationAction = createAsyncThunk(
  'automations/getAbandonedOrderFormAutomation',
  getAbandonedOrderFormAutomation,
);

const automationsSlice = createSlice({
  name: 'automations',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAutomationsAction.pending, state => {
        state.loading = true;
      })
      .addCase(getAutomationsAction.fulfilled, (state, action) => {
        state.automations = action.payload;
        state.hasLoaded = true;
        state.loading = false;
      })
      .addCase(getAutomationsAction.rejected, handleReducerError('Error Getting Automations'))

      //thank you
      .addCase(getThankYouAutomationAction.pending, state => {
        state.loading = true;
      })
      .addCase(getThankYouAutomationAction.fulfilled, (state, action) => {
        state.automations.thankYou = action.payload;
        state.hasLoaded = true;
        state.loading = false;
      })
      .addCase(getThankYouAutomationAction.rejected, handleReducerError('Error Getting Thank You Automation'))
      //newsletter
      .addCase(getNewsletterAutomationAction.pending, state => {
        state.loading = true;
      })
      .addCase(getNewsletterAutomationAction.fulfilled, (state, action) => {
        state.automations.newsletter = action.payload;
        state.hasLoaded = true;
        state.loading = false;
      })
      .addCase(getNewsletterAutomationAction.rejected, handleReducerError('Error Getting Newsletter Automation'))
      //welcome series
      .addCase(getWelcomeSeriesAutomationAction.pending, state => {
        state.loading = true;
      })
      .addCase(getWelcomeSeriesAutomationAction.fulfilled, (state, action) => {
        state.automations.welcomeSeries = action.payload;
        state.hasLoaded = true;
        state.loading = false;
      })
      .addCase(getWelcomeSeriesAutomationAction.rejected, handleReducerError('Error Getting Welcome Series Automation'))
      //abandoned checkout
      .addCase(getAbandonedCheckoutAutomationAction.pending, state => {
        state.loading = true;
      })
      .addCase(getAbandonedCheckoutAutomationAction.fulfilled, (state, action) => {
        state.automations.abandonedCheckout = action.payload;
        state.hasLoaded = true;
        state.loading = false;
      })
      .addCase(
        getAbandonedCheckoutAutomationAction.rejected,
        handleReducerError('Error Getting Abandoned Checkout Automation'),
      )
      //abandoned order form
      .addCase(getAbandonedOrderFormAutomationAction.pending, state => {
        state.loading = true;
      })
      .addCase(getAbandonedOrderFormAutomationAction.fulfilled, (state, action) => {
        state.automations.abandonedOrderForm = action.payload;
        state.hasLoaded = true;
        state.loading = false;
      })
      .addCase(
        getAbandonedOrderFormAutomationAction.rejected,
        handleReducerError('Error Getting Abandoned Order Form Automation'),
      );
  },
});

export default automationsSlice.reducer;
